/* eslint-disable max-lines-per-function */
import Button from "@atlaskit/button";
import Select from "@atlaskit/select";
import Textfield from "@atlaskit/textfield";
import { ReactElement, useContext, useState } from "react";
import { MessageContext } from "../../contexts/Message/MessageContext";
import APIService from "../../services/API";
import { SelectOpton } from "../../types/SelectedOption";
import "./DeleteUserAccount.scss";

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
interface Props {}

interface UserResponse {
    id: string;
    firstName: string;
    lastName: string;
}

interface CoachDeleteLookupResponse {
    userId: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    insuranceName?: string;
    isPendingDeletion: boolean;
    daysUntilDeletion: number | null;
    daysToDeleteAccount: number;
}

export default function DeleteUserAccount({}: Props): ReactElement {
    const [codeOrEmail, setCodeOrEmail] = useState("");
    const [valid, setValid] = useState(false);
    const [users, setUsers] = useState<UserResponse[]>([]);
    const [selectedUser, setSelectedUser] = useState<SelectOpton | null>(
        null
    );
    const messageContext = useContext(MessageContext);

    const showSuccessMessage = (title: string) => {
        messageContext.actions.setMessage({
            title,
            body: undefined,
            titleApparencce: undefined,
            isOpen: true,
            buttons: [
                {
                    buttonText: "OK",
                    onClick: () => messageContext.actions.closeModal(),
                },
            ],
        });
    };

    const showErrorMessage = (title: string) => {
        messageContext.actions.showErrorMessage(title);
    };

    const resetSearch = () => {
        setUsers([]);
        setSelectedUser(null);
        setCodeOrEmail("");
        setValid(false);
    };

    const fetchUsers = () => {
        APIService.handlePostRequest<{ codeOrEmail: string }, UserResponse[]>(
            { codeOrEmail },
            `/users/users_by_code_or_email`
        )
            .then((usersList) => {
                setUsers(usersList);
                setSelectedUser(null);
            })
            .catch((err) => {
                setUsers([]);
                if (err.response?.status === 404) {
                    showErrorMessage("Nutzer konnte nicht gefunden werden");
                } else {
                    showErrorMessage(
                        "Etwas ging schief. Bitte versuchen Sie es erneut"
                    );
                }
            });
    };

    const performDelete = (userId: string) => {
        APIService.handleDeleteRequest<null, { success: boolean }>(
            null,
            `/users/coach-delete/${userId}`
        )
            .then(() => {
                messageContext.actions.closeModal();
                showSuccessMessage(
                    "Der Nutzer wurde zur Löschung vorgemerkt."
                );
                resetSearch();
            })
            .catch((err) => {
                messageContext.actions.closeModal();
                if (err.response?.status === 403) {
                    showErrorMessage("Keine Berechtigung für diese Aktion.");
                } else if (err.response?.status === 404) {
                    showErrorMessage("Nutzer nicht gefunden.");
                } else {
                    showErrorMessage(
                        "Etwas ging schief. Der Nutzer konnte nicht gelöscht werden."
                    );
                }
            });
    };

    const performRevert = (userId: string) => {
        APIService.handlePatchRequest<null, { success: boolean }>(
            null,
            `/users/coach-delete/${userId}/revert`
        )
            .then(() => {
                messageContext.actions.closeModal();
                showSuccessMessage(
                    "Die Löschung wurde rückgängig gemacht."
                );
                resetSearch();
            })
            .catch((err) => {
                messageContext.actions.closeModal();
                if (err.response?.status === 400) {
                    showErrorMessage("Nutzer ist nicht zur Löschung vorgemerkt.");
                } else if (err.response?.status === 403) {
                    showErrorMessage("Keine Berechtigung für diese Aktion.");
                } else if (err.response?.status === 404) {
                    showErrorMessage("Nutzer nicht gefunden.");
                } else if (err.response?.status === 500) {
                    /*
                     * Covers the partial-revert case: the account is no longer
                     * scheduled for deletion, but is still deactivated in
                     * Keycloak. The coach must know the account is not usable
                     * yet, rather than being told it worked.
                     */
                    showErrorMessage(
                        "Die Löschung wurde nur teilweise rückgängig gemacht. Der Account ist nicht mehr zur Löschung vorgemerkt, konnte aber nicht wieder aktiviert werden. Bitte erneut versuchen."
                    );
                } else {
                    showErrorMessage(
                        "Etwas ging schief. Bitte versuchen Sie es erneut."
                    );
                }
            });
    };

    const describeUser = (lookup: CoachDeleteLookupResponse): string => {
        const name = `${lookup.firstName || "-"} ${lookup.lastName || "-"}`;
        const email = lookup.email || "-";
        const insuranceName = lookup.insuranceName || "-";
        return `Name: ${name}, E-Mail: ${email}, Krankenkasse: ${insuranceName}.`;
    };

    const openPendingDeletionModal = (lookup: CoachDeleteLookupResponse) => {
        messageContext.actions.setMessage({
            title: "Nutzer ist bereits zur Löschung vorgemerkt",
            body: `${describeUser(lookup)} Verbleibende Tage bis zur endgültigen, unwiderruflichen Löschung: ${lookup.daysUntilDeletion} von ${lookup.daysToDeleteAccount}.`,
            isOpen: true,
            buttons: [
                {
                    buttonText: "Abbrechen",
                    onClick: () => messageContext.actions.closeModal(),
                },
                {
                    buttonText: "Löschung rückgängig machen",
                    buttonApperance: "warning",
                    onClick: () => performRevert(lookup.userId),
                },
            ],
        });
    };

    const openConfirmDeleteModal = (lookup: CoachDeleteLookupResponse) => {
        messageContext.actions.setMessage({
            title: "Nutzer wirklich löschen?",
            body: `${describeUser(lookup)} Der Account wird zunächst für ${lookup.daysToDeleteAccount} Tage deaktiviert (keine Anmeldung mehr möglich), damit die Abrechnung mit der Krankenkasse noch abgeschlossen werden kann. Erst danach werden der Account und alle zugehörigen Daten endgültig und unwiderruflich gelöscht. Bitte prüfen Sie zunächst, ob der/die Nutzer:in das Konto selbst über die App löschen kann - das ist der bevorzugte Weg.`,
            isOpen: true,
            buttons: [
                {
                    buttonText: "Abbrechen",
                    onClick: () => messageContext.actions.closeModal(),
                },
                {
                    buttonText: "Nutzer löschen",
                    buttonApperance: "danger",
                    onClick: () => performDelete(lookup.userId),
                },
            ],
        });
    };

    const openDeleteFlow = (user: SelectOpton) => {
        setSelectedUser(user);
        APIService.HandleGetRequest<CoachDeleteLookupResponse>(
            `/users/coach-delete/${user.value}`
        )
            .then((lookup) => {
                if (lookup.isPendingDeletion) {
                    openPendingDeletionModal(lookup);
                } else {
                    openConfirmDeleteModal(lookup);
                }
            })
            .catch(() => {
                showErrorMessage("Nutzerdaten konnten nicht geladen werden.");
            });
    };

    return (
        <div className="form-wrapper">
            <div className="deleteUserAccount-inputs-cont">
                <div className="deleteUserAccount-email-cont">
                    <h3>Identifikationscode oder E-Mail Adresse</h3>
                    <Textfield
                        value={codeOrEmail || ""}
                        type="text"
                        onChange={(event) => {
                            /*
                             * Attlaskit has bad types here
                             */
                            // @ts-expect-error - Atlaskit types problem
                            const value = event.target.value;

                            setValid(
                                value?.length === 5 || value?.includes("@")
                            );
                            setCodeOrEmail(value.trim());
                        }}
                        placeholder=""
                    />
                    <br />
                    <Button
                        isDisabled={!valid}
                        appearance="primary"
                        onClick={fetchUsers}
                    >
                        Suche nach Benutzer
                    </Button>
                </div>
                {users.length > 0 && (
                    <div className="deleteUserAccount-email-cont">
                        <h3>Benutzer auswählen</h3>
                        <Select
                            value={selectedUser}
                            type="text"
                            options={users.map((user) => ({
                                label: `${user.firstName || "-"} ${
                                    user.lastName || "-"
                                }`,
                                value: user.id,
                            }))}
                            onChange={(event) => {
                                if (event) {
                                    openDeleteFlow(event);
                                }
                            }}
                            placeholder=""
                        />
                    </div>
                )}
            </div>
        </div>
    );
}
